/**
 * fmtText — formattatore canonico per testo normativo EU AML/CFT.
 *
 * Livelli strutturali EU gestiti:
 *   L1 Paragrafo:      "1. Testo..." / "2. Testo..."  → parasHtml (esterno)
 *   L2 Definizioni:    "(N) 'termine' means..."        → <ol class="def-list">
 *   L3 Punti:          "(a)(b)(c) testo"               → <ul>
 *   L4 Sotto-punti:    "(i)(ii)(iii) testo"            → <ul> nested
 *
 * Fonte della struttura:
 *   EUR-Lex CELLAR usa tabelle a 2 colonne per ogni livello:
 *     <td>(N)</td><td>testo</td>
 *   parseIntoParas combina le righe marker+testo e preserva \n prima di (
 *   → il testo arriva a fmtText già strutturato con \n prima di ogni marker
 *
 * ROMAN_RE — pattern preciso per romani (i)–(xx):
 *   REGOLA: iv/ix/xiv devono venire PRIMA di i{1,3}/xi{1,3} nell'alternation
 *   per evitare match parziale. Non modificare senza aggiornare FIXES_LEARNED AP-12.
 */

const ROMAN_PAT = 'i{1,3}|iv|vi{0,3}|ix|xi{0,3}|xiv|xvi{0,3}|xix|xx';
const ROMAN_RE  = new RegExp(`^\\((${ROMAN_PAT})\\)\\s`, 'i');
const NUM_RE    = /^\(\d+\)\s/;
const LETTER_RE = /^\([a-z]\)\s/;

/** Identifica il tipo di marker all'inizio di una riga */
function markerLevel(line: string): 'num' | 'letter' | 'roman' | 'none' {
  if (NUM_RE.test(line))    return 'num';
  if (ROMAN_RE.test(line))  return 'roman';
  if (LETTER_RE.test(line)) return 'letter';
  return 'none';
}

/** Renderizza una lista di righe strutturate in HTML annidato */
function renderLines(lines: string[]): string {
  if (lines.length === 0) return '';

  // Raggruppa per livello: num → def-list, letter → ul, roman → ul nested
  const firstLevel = markerLevel(lines[0]);

  if (firstLevel === 'none') {
    // Testo introduttivo seguito da sotto-lista
    const splitIdx = lines.findIndex((l, i) => i > 0 && markerLevel(l) !== 'none');
    if (splitIdx < 0) return lines.map(l => `<p>${l}</p>`).join('');
    const intro = lines.slice(0, splitIdx).map(l => `<p>${l}</p>`).join('');
    return intro + renderLines(lines.slice(splitIdx));
  }

  const tag = firstLevel === 'num' ? 'ol class="def-list"' : 'ul';
  const closeTag = firstLevel === 'num' ? 'ol' : 'ul';

  const items: string[] = [];
  let i = 0;
  while (i < lines.length) {
    const line = lines[i];
    const level = markerLevel(line);
    if (level === 'none') { items.push(`<li>${line}</li>`); i++; continue; }

    // Collect sub-lines belonging to this item
    // EU rule: sub-items only follow a parent that ends with ':'
    //   '(e) ...in particular:'  → colon → (i)(ii) are roman sub-items
    //   '(h) ...intermediaries;' → semicolon → (i) is the 9th lettered peer
    //   '(5) means:'             → colon → (a)(b) are letter sub-items
    const parentEndsColon = line.trimEnd().endsWith(':');
    const subLines: string[] = [];
    i++;
    while (i < lines.length) {
      const subLevel = markerLevel(lines[i]);
      const isChild = parentEndsColon && (
        (level === 'num'    && (subLevel === 'letter' || subLevel === 'roman'))
     || (level === 'letter' &&  subLevel === 'roman')
      );
      if (!isChild) break;
      subLines.push(lines[i]);
      i++;
    }

    if (subLines.length > 0) {
      items.push(`<li>${line}${renderLines(subLines)}</li>`);
    } else {
      items.push(`<li>${line}</li>`);
    }
  }

  return `<${tag}>${items.join('')}</${closeTag}>`;
}

/**
 * Formatta il testo di un paragrafo normativo per la visualizzazione HTML.
 * Usato in ArticleBody.tsx per ogni p.text.
 */
export function fmtText(text: string): string {
  // Structured text: has \n before ( markers (from parseIntoParas + CELLAR tables)
  if (/\n\(/.test(text)) {
    const lines = text.split('\n').map(l => l.trim()).filter(Boolean);
    return renderLines(lines);
  }

  // Legacy fallback for old data or flat text (no \n structure)
  // Split on ; before [a-hj-uwyz] (peer items) or : before [a-h] (first item)
  const allRe = /(?<=;)\s*(?=\([a-hj-uwyz]\)\s)|(?<=:)\s*(?=\([a-h]\)\s)/g;
  const allParts = text.split(allRe);
  if (allParts.length <= 1) return `<p>${text}</p>`;

  const DEEP_SUB_RE = new RegExp(`(?=\\s\\((?:${ROMAN_PAT})\\)\\s)`, 'gi');
  const items: string[] = [];
  let idx = 1;
  while (idx < allParts.length) {
    const part = allParts[idx];
    const deepParts = part.split(DEEP_SUB_RE);
    if (deepParts.length > 1) {
      items.push(`<li>${deepParts[0].trim()}<ul>${deepParts.slice(1).map((s: string) => `<li>${s.trim()}</li>`).join('')}</ul></li>`);
    } else {
      items.push(`<li>${part.trim()}</li>`);
    }
    idx++;
  }
  return `<p>${allParts[0]}</p><ul>${items.join('')}</ul>`;
}
